import React, { useEffect, useState } from 'react'

import { getTheme } from 'themes'
import { ThemeProvider, useMediaQuery } from '@mui/material'

import { useSurveyActions } from 'redux/actions/Surveys'
import { useShowNps } from 'redux/hooks/NPS'
import { useTranslation } from 'language/translator'

import MUIButtonGroup from '@mui/material/ButtonGroup'
import MUIFormLabel from '@mui/material/FormLabel'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import FormControl from 'components_new/atoms/FormControl'
import IconButton from 'components_new/atoms/IconButton'
import Icon from 'components_new/atoms/Icon'
import Text from 'components_new/atoms/Text'
import TextField from 'components_new/atoms/TextField'

// 20 seconds
const DELAY = 1000 * 20

const NPSSurvey = () => {
  const { tryCreateSurvey, tryGetShowNps } = useSurveyActions()

  const { t } = useTranslation()
  const theme = getTheme('dark')
  const isMobile = useMediaQuery(theme.breakpoints.down('mobile'), {
    noSsr: true
  })

  const [score, setScore] = useState<number | null>(null)
  const [feedback, setFeedback] = useState<string>('')
  const [submitted, setSubmitted] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [showDialog, setShowDialog] = useState<boolean>(false)

  const { show, refetch } = useShowNps()

  // Initial fetch on mount
  useEffect(() => {
    tryGetShowNps()
  }, [])

  // Set up polling when refetch is true
  useEffect(() => {
    if (!refetch) return

    const intervalId = setInterval(() => {
      tryGetShowNps()
    }, 1000 * 60) // 1 minute

    return () => clearInterval(intervalId)
  }, [refetch, tryGetShowNps])

  useEffect(() => {
    if (show && !isMobile) {
      const timer = setTimeout(() => {
        setShowDialog(true)
      }, DELAY)

      return () => clearTimeout(timer)
    }

    return () => {}
  }, [show, isMobile])

  if (!showDialog) return null

  const handleScoreChange = (value: number) => {
    setScore(value)
  }

  const handleFeedbackChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFeedback(e.target.value)
  }

  const handleClose = () => {
    if (!submitted) {
      // snooze NPS with snooze
      tryCreateSurvey({ action: 'snooze' })
    }

    setShowDialog(false)
  }

  const handleSubmit = () => {
    setLoading(true)

    tryCreateSurvey(
      { score: score as number, response: feedback || null, action: 'answer' },
      () => {
        setLoading(false)
        setSubmitted(true)

        // automatically close after 5s.
        setTimeout(() => {
          setShowDialog(false)
        }, 1000 * 5)
      }
    )
  }

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          width: '100%',
          zIndex: 1300,
          display: 'flex',
          justifyContent: 'flex-end',
          p: 2
        }}
      >
        <Box
          sx={{
            bgcolor: 'background.default',
            borderRadius: 2,
            p: 2,
            width: '100%',
            maxWidth: 450,
            boxShadow: 24
          }}
        >
          <IconButton
            onClick={handleClose}
            sx={{ position: 'absolute', top: 12, right: 12 }}
          >
            <Icon name="Close" fontSize="small" />
          </IconButton>

          {submitted ? (
            <FormControl fullWidth>
              <Text variant="body1" align="center">
                {t('nps', 'thank_you_text')}
              </Text>
            </FormControl>
          ) : (
            <FormControl fullWidth>
              <MUIFormLabel component="legend">
                <Text variant="h6">{t('nps', 'score_question')}</Text>
              </MUIFormLabel>

              <MUIButtonGroup sx={{ mt: 2, mb: 2 }} fullWidth>
                {Array.from({ length: 10 }, (_, i) => i + 1).map((num) => (
                  <Button
                    key={num}
                    variant={score === num ? 'contained' : 'outlined'}
                    onClick={() => handleScoreChange(num)}
                    disabled={loading}
                  >
                    {num}
                  </Button>
                ))}
              </MUIButtonGroup>

              {score !== null && (
                <Box sx={{ mb: 2 }}>
                  <TextField
                    label={t('nps', 'reason_question')}
                    multiline
                    rows={3}
                    fullWidth
                    value={feedback}
                    onChange={handleFeedbackChange}
                    disabled={loading}
                  />
                </Box>
              )}

              <Box sx={{ textAlign: 'right', mt: 2 }}>
                <Button variant="text" onClick={handleClose} disabled={loading}>
                  {loading ? t('nps', 'skip_loading') : t('nps', 'skip')}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={!score || loading}
                  loading={loading}
                  loadingTitle={t('nps', 'submit_loading')}
                >
                  {t('nps', 'submit')}
                </Button>
              </Box>
            </FormControl>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  )
}

export default NPSSurvey
